import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import ls from "localstorage-slim";
import { doc, getDoc } from "firebase/firestore";
import { firebaseDB } from "../../../firebase-analytics";

const CACHE_KEY = "pricing_data";
const CACHE_TTL_SECONDS = 86400; // 24 hours (1 day)

export const DEFAULT_PRICING_CONFIG = {
  country: "LK",
  countryName: "Sri Lanka",
  currency: "LKR",
  phoneCode: "+94",
  plans: {
    starter: { monthly: 4900, yearly: 3900 },
    clinic: { monthly: 9900, yearly: 7900 },
    enterprise: { monthly: null, yearly: null },
  },
};

const PricingContext = createContext({
  country: DEFAULT_PRICING_CONFIG.country,
  countryName: DEFAULT_PRICING_CONFIG.countryName,
  currency: DEFAULT_PRICING_CONFIG.currency,
  phoneCode: DEFAULT_PRICING_CONFIG.phoneCode,
  plans: DEFAULT_PRICING_CONFIG.plans,
  getPlanPrice: () => null,
  formatPrice: () => "",
  loading: false,
});

export const usePricingContext = () => useContext(PricingContext);

export async function getCountryCode(timeoutMs = 3000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch("https://ipapi.co/country_code/", {
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`ipapi HTTP status: ${response.status}`);
    }

    const code = (await response.text()).trim();
    return code ? code.toUpperCase() : null;
  } catch (error) {
    clearTimeout(timeoutId);
    console.warn(
      "Could not retrieve country code from ipapi.co:",
      error?.message || error,
    );
    return null;
  }
}

export async function fetchPricingForCountry(countryCode) {
  try {
    if (countryCode) {
      const docRef = doc(firebaseDB, "pricing", countryCode);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return docSnap.data();
      }
    }

    // Fallback to DEFAULT document in Firestore
    const defaultDocRef = doc(firebaseDB, "pricing", "DEFAULT");
    const defaultDocSnap = await getDoc(defaultDocRef);
    if (defaultDocSnap.exists()) {
      return defaultDocSnap.data();
    }
  } catch (error) {
    console.warn(
      "Failed to fetch pricing from Firestore:",
      error?.message || error,
    );
  }
  return null;
}

export const PricingContextProvider = ({ children }) => {
  const [pricingConfig, setPricingConfig] = useState(() => {
    try {
      const cached = ls.get(CACHE_KEY);
      if (cached && cached.country && cached.plans) {
        return cached;
      }
    } catch {
      // In case localStorage is blocked/unavailable
    }
    return DEFAULT_PRICING_CONFIG;
  });

  const [loading, setLoading] = useState(() => {
    try {
      const cached = ls.get(CACHE_KEY);
      return !(cached && cached.country && cached.plans);
    } catch {
      return false;
    }
  });

  useEffect(() => {
    let isMounted = true;

    async function initializePricing() {
      try {
        const cached = ls.get(CACHE_KEY);
        if (cached && cached.country && cached.plans) {
          if (isMounted) {
            setPricingConfig(cached);
            setLoading(false);
          }
          return;
        }
      } catch (err) {
        console.warn("Error reading cache:", err);
      }

      const countryCode = await getCountryCode();

      const firestoreData = await fetchPricingForCountry(countryCode);
      const resolvedPricing = firestoreData || DEFAULT_PRICING_CONFIG;

      try {
        ls.set(CACHE_KEY, resolvedPricing, { ttl: CACHE_TTL_SECONDS });
      } catch (err) {
        console.warn("Error caching pricing:", err);
      }

      if (isMounted) {
        setPricingConfig(resolvedPricing);
        setLoading(false);
      }
    }

    initializePricing();

    return () => {
      isMounted = false;
    };
  }, []);

  const getPlanPrice = useCallback(
    (planName, isYearly = false) => {
      if (!planName) return null;
      const normalizedKey = planName.toLowerCase();
      const plan = pricingConfig?.plans?.[normalizedKey];
      if (!plan) return null;
      return isYearly ? plan.yearly : plan.monthly;
    },
    [pricingConfig],
  );

  const formatPrice = useCallback(
    (amount) => {
      if (amount === null || amount === undefined) return "Custom";
      return `${pricingConfig.currency} ${Number(amount).toLocaleString()}`;
    },
    [pricingConfig.currency],
  );

  const value = useMemo(
    () => ({
      country: pricingConfig.country,
      countryName: pricingConfig.countryName,
      currency: pricingConfig.currency,
      phoneCode: pricingConfig.phoneCode,
      plans: pricingConfig.plans,
      getPlanPrice,
      formatPrice,
      loading,
    }),
    [pricingConfig, getPlanPrice, formatPrice, loading],
  );

  return (
    <PricingContext.Provider value={value}>{children}</PricingContext.Provider>
  );
};
